import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProducerSettingsPage.module.css';
import ProducerContentContainer from '../layout/ProducerContentContainer';
import SettingsMenu from '../../../shared/settings/SettingsMenu';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import NotificationSettings from '../../../shared/settings/NotificationSettings';
import UpgradeAccountSection from './UpgradeAccountSection';
import PrivacySettings from '../../../shared/settings/PrivacySettings';
import FindPasswordModal from '../../../shared/settings/FindPasswordModal';
import { getMyProducerProfile, updateProducerProfile } from '../../../api/producerApi';
import { C, t } from '../../../shared/constants/dashboardConstants';
import { useAuth } from '../../../contexts/AuthContext';

/* ── Editable Info Card ── */
function PencilIcon({ onClick, disabled }) {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      onClick={disabled ? undefined : onClick}
      style={{ cursor: disabled ? 'default' : 'pointer', flexShrink: 0 }}
    >
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke={disabled ? C.g300 : C.g400} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke={disabled ? C.g300 : C.g400} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function EditableInfoCard({ label, value, onSave, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const handleEdit = () => { setDraft(value || ''); setEditing(true); };
  const handleCancel = () => setEditing(false);
  const handleSave = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  if (editing) {
    return (
      <div style={{
        minHeight: '108px', boxSizing: 'border-box',
        background: C.white, border: `1px solid ${C.bk}`,
        borderRadius: '12px', padding: '20px 24px',
        display: 'flex', flexDirection: 'column', gap: '12px',
      }}>
        <span style={t('16px', 400, C.sub)}>{label}</span>
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%', height: '38px', padding: '0 12px',
            boxSizing: 'border-box',
            background: C.bg, border: `1px solid ${C.g200}`,
            borderRadius: '8px', outline: 'none',
            ...t('16px', 400, C.bk),
          }}
        />
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={handleCancel} style={{
            height: '32px', padding: '0 14px',
            background: C.g200, border: 'none',
            borderRadius: '8px', cursor: 'pointer',
            ...t('14px', 500, C.bk),
          }}>취소</button>
          <button onClick={handleSave} disabled={saving} style={{
            height: '32px', padding: '0 14px',
            background: saving ? C.g300 : C.bk, border: 'none',
            borderRadius: '8px', cursor: saving ? 'default' : 'pointer',
            ...t('14px', 500, C.white),
          }}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: '108px', minWidth: 0, boxSizing: 'border-box',
      background: C.white, border: `1px solid ${C.g200}`,
      borderRadius: '12px', padding: '20px 24px',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', minWidth: 0 }}>
        <span style={t('16px', 400, C.sub)}>{label}</span>
        <PencilIcon onClick={handleEdit} disabled={readOnly} />
      </div>
      <span style={{ ...t('18px', 500, C.bk), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || '—'}</span>
    </div>
  );
}

/* ── Producer Account Settings ── */
function ProducerAccountSettings({ profile, onSave, isLoading, onOpenPasswordModal, canAddTalent, onAddTalent }) {
  if (isLoading) {
    return (
      <div style={{ background: C.white, border: `1px solid ${C.g200}`, borderRadius: '20px', padding: '30px', display: 'flex', justifyContent: 'center' }}>
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  return (
    <div style={{
      background: C.white, border: `1px solid ${C.g200}`,
      borderRadius: '20px', padding: '30px',
      display: 'flex', flexDirection: 'column', gap: '30px',
    }}>
      {/* 프로필 관리 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <span style={t('18px', 600, C.bk)}>프로필 관리</span>
        <div style={{
          background: C.white, border: `1px solid ${C.g200}`,
          borderRadius: '12px', padding: '24px 30px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={t('18px', 600, C.bk)}>활성화된 프로필</span>
              <div style={{ padding: '3px 10px', borderRadius: '20px', background: C.g200 }}>
                <span style={t('13px', 500, C.bk)}>제작사</span>
              </div>
            </div>
            <span style={t('14px', 400, C.sub)}>한 계정에서 배우와 제작사 프로필을<br />모두 사용할 수 있습니다</span>
          </div>
          {canAddTalent && (
            <button onClick={onAddTalent} className={styles.addAccountBtn}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4" stroke={C.g500} strokeWidth="1.8"/>
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={C.g500} strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M19 8v6M22 11h-6" stroke={C.g500} strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
              배우 계정<br />추가하기
            </button>
          )}
        </div>
      </div>

      {/* 제작사 정보 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <span style={t('18px', 600, C.bk)}>제작사 정보</span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <EditableInfoCard
            label="제작사명"
            value={profile?.companyName}
            onSave={(v) => onSave({ companyName: v })}
          />
          <EditableInfoCard
            label="담당자 이름"
            value={profile?.managerName}
            onSave={(v) => onSave({ managerName: v })}
          />
          <EditableInfoCard
            label="전화번호"
            value={profile?.phone || '—'}
            onSave={(v) => onSave({ phone: v })}
            readOnly
          />
          <EditableInfoCard
            label="이메일"
            value={profile?.contactEmail}
            onSave={(v) => onSave({ contactEmail: v })}
          />
          <EditableInfoCard
            label="웹사이트"
            value={profile?.website}
            onSave={(v) => onSave({ website: v })}
          />
          <EditableInfoCard
            label="주소"
            value={profile?.address}
            onSave={(v) => onSave({ address: v })}
          />
          <EditableInfoCard
            label="사업자 등록번호"
            value={profile?.businessRegNumber}
            onSave={(v) => onSave({ businessRegNumber: v })}
          />
          <div style={{
            height: '108px', boxSizing: 'border-box',
            background: C.white, border: `1px solid ${C.g200}`,
            borderRadius: '12px', padding: '20px 24px',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
          }}>
            <span style={t('16px', 400, C.sub)}>비밀번호</span>
            <button
              onClick={onOpenPasswordModal}
              style={{
                alignSelf: 'flex-start', height: '34px', padding: '0 16px',
                background: C.g200, border: 'none', borderRadius: '8px',
                cursor: 'pointer', ...t('14px', 600, C.bk),
              }}
            >
              비밀번호 변경
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Producer Billing Settings ── */
function ProducerBillingSettings() {
  return (
    <div style={{
      background: C.white, border: `1px solid ${C.g200}`,
      borderRadius: '20px', padding: '30px',
      display: 'flex', flexDirection: 'column', gap: '30px',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <span style={t('18px', 600, C.bk)}>현재 플랜</span>
        <div style={{
          background: C.white, border: `1px solid ${C.g200}`,
          borderRadius: '12px', padding: '24px 30px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={t('18px', 600, C.bk)}>비지니스 플랜</span>
              <div style={{ padding: '3px 10px', borderRadius: '20px', background: '#E8F5E9' }}>
                <span style={t('13px', 500, '#2E7D32')}>활성</span>
              </div>
            </div>
            <span style={t('16px', 400, C.bk)}>월 79,000원</span>
            <span style={t('14px', 400, C.sub)}>다음 결제일: 2024년 2월 15일</span>
          </div>
          <button style={{
            height: '50px', padding: '0 24px',
            background: C.g200, border: 'none',
            borderRadius: '10px', cursor: 'pointer',
            ...t('16px', 500, C.bk),
          }}>플랜 변경</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <span style={t('18px', 600, C.bk)}>결제 수단</span>
        <div style={{
          background: C.white, border: `1px solid ${C.g200}`,
          borderRadius: '12px', padding: '24px 30px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={t('16px', 500, C.bk)}>신용카드</span>
            <span style={t('14px', 400, C.sub)}>···· ···· ···· 1234</span>
          </div>
          <button style={{
            height: '44px', padding: '0 20px',
            background: 'transparent', border: `1px solid ${C.g200}`,
            borderRadius: '10px', cursor: 'pointer',
            ...t('15px', 500, C.bk),
          }}>카드 변경</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <span style={t('18px', 600, C.bk)}>결제 내역</span>
        <div style={{
          background: C.white, border: `1px solid ${C.g200}`,
          borderRadius: '12px', padding: '20px 30px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
        }}>
          <span style={t('16px', 500, C.bk)}>결제 내역 보기</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke={C.bk} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
const PANEL_INFO = {
  '계정설정':    { title: '계정 설정',    subtitle: '제작사 계정 정보와 보안 설정을 관리합니다.' },
  '알림설정':    { title: '알림 설정',    subtitle: '캐스팅 공고 및 메시지 알림을 관리합니다.' },
  '개인정보보호': { title: '개인정보 보호', subtitle: '프로필 공개 범위 및 데이터를 관리합니다.' },
  '결제및구독':   { title: '결제 및 구독', subtitle: '결제 수단 및 구독 플랜을 관리합니다.' },
};

export default function ProducerSettingsPage() {
  const navigate = useNavigate();
  const { isTalent } = useAuth();
  const [active, setActive] = useState('계정설정');
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getMyProducerProfile();
      setProfile(data);
    } catch {
      // 로드 실패 시 빈 상태 유지
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveProfile = async (changed) => {
    if (!profile) return;
    const payload = {
      id: profile.id,
      companyName: profile.companyName,
      managerName: profile.managerName,
      phone: profile.phone,
      contactEmail: profile.contactEmail,
      website: profile.website,
      address: profile.address,
      businessRegNumber: profile.businessRegNumber,
      logoFileId: profile.logoFileId ?? null,
      ...changed,
    };
    await updateProducerProfile(payload);
    setProfile(prev => ({ ...prev, ...changed }));
  };

  const { title, subtitle } = PANEL_INFO[active];

  const renderPanel = () => {
    switch (active) {
      case '계정설정':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* 대행 계정에만 노출 — 일반 계정에서는 컴포넌트가 스스로 null을 반환한다 */}
            <UpgradeAccountSection />
            <ProducerAccountSettings
            profile={profile}
            onSave={handleSaveProfile}
            isLoading={isLoading}
            onOpenPasswordModal={() => setShowPasswordModal(true)}
            canAddTalent={!isTalent}
            onAddTalent={() => navigate('/onboarding/talent')}
            />
          </div>
        );
      case '알림설정':
        // 제작사 알림 설정을 저장·조회할 API가 아직 없다. 불러올 것도 저장할 곳도 없음을 명시한다.
        return <NotificationSettings initial={null} />;
      case '개인정보보호':
        return <PrivacySettings />;
      case '결제및구독':
        return <ProducerBillingSettings />;
      default:
        return null;
    }
  };

  return (
    <ProducerContentContainer>
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <h1 className={styles.pageTitle}>계정 설정</h1>
          <p className={styles.pageSubtitle}>제작사 계정 정보와 세부 설정을 관리합니다.</p>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.layout}>
          <SettingsMenu active={active} onSelect={setActive} />

          <div className={styles.panelContent}>
            <div className={styles.panelHeader}>
              <span className={styles.panelTitle}>{title}</span>
              <span className={styles.panelSubtitle}>{subtitle}</span>
            </div>
            {renderPanel()}
          </div>
        </div>
      </div>

      {showPasswordModal && <FindPasswordModal onClose={() => setShowPasswordModal(false)} />}
    </ProducerContentContainer>
  );
}
